/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import AppContainer from '@/components/layout/container';
import { Api } from '@/api';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import s from './styles.module.scss';
import RecitersFeaturedCarousel from '@/components/reciters/featured-carousel';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import ReadSurahsList from '@/components/read-surahs/list';
import { IAyat } from '@/infrastructure/interfaces/i-ayat';
import ReadBookmarksCarousel from '@/components/read-surahs/bookmarks-carousel';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { ReadBookmark } from '@/infrastructure/class/read-bookmark';
import Head from 'next/head';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import { useRouter } from 'next/router';

const api = Api.getInstance();

const SearchPage: NextLayoutComponentType = () => {
  const [reciters, setReciters] = useState<IReciter[]>([]);
  const [readSurahs, setReadSurahs] = useState<IReadSurah[]>([]);
  const [ayas, setAyas] = useState<IAyat[]>([]);
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const { search } = router.query;

  const { t: tMeta } = useTranslation('meta');
  const { t: tCommon } = useTranslation('common');
  const { t: tLinks } = useTranslation('links');

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const ayasItems = useMemo(() => {
    return sortArrayByFieldName(
      ayas.map(
        (ayat) =>
          new ReadBookmark({
            ayaIndex: ayat.number,
            page: ayat.page,
            suraAName: String(ayat.surah?.title_ar),
            suraIndex: Number(ayat.surah?.index),
            suraTName: String(ayat.surah?.transliteration),
            readSurah: ayat.surah,
          })
      ),
      'ayaIndex'
    );
  }, [ayas]);

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('search'),
      path: routes.search,
    },
  ];

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  useEffect(() => {
    if (!search) {
      setReciters([]);
      setReadSurahs([]);
      setAyas([]);
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        const [recitersResult, readSurahsResult, ayasResult] =
          await Promise.all([
            api.searchReciters(`${search}`, allRecordsPaginationOptions),
            api.searchReadSurahs(`${search}`, allRecordsPaginationOptions),
            api.searchAyas(`${search}`, allRecordsPaginationOptions),
          ]);

        recitersResult.data.results.map((reciter) => {
          addDomainForReciterImages(reciter);
        });

        setReciters(recitersResult.data.results);
        setReadSurahs(readSurahsResult.data.results);
        setAyas(ayasResult.data.results);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [search]);

  return (
    <>
      <Head>
        <title>{tMeta('searchPageTitle')}</title>
        <meta name="description" content={tMeta('searchPageDescription')} />
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <main>
        <AppContainer>
          <RecitersFeaturedCarousel
            className={s['featured-carousel']}
            items={reciters}
            secondType
          />
          <ReadSurahsList
            items={readSurahs}
            className={s['read-surah']}
            bordered
          />
          <ReadBookmarksCarousel
            className={s['read-carousel']}
            items={ayasItems}
            title={tCommon('ayahs')}
          />
          {loading && <p>Loading...</p>}
        </AppContainer>
      </main>
    </>
  );
};

SearchPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, localeList)),
    },
  };
};

export default SearchPage;
